import { SyntheticEvent, useEffect, useState } from "react";
import {
	useAlertContext,
	useClinicContext,
	useUserContext
} from "../../contexts";
import { BuildUrl } from "../../helpers";
import {
	apiAccessRequest,
	SETTING_OM_ENABLE_MEMBERSHIP
} from "../../constants";
import Switch from "@mui/material/Switch";
import { useMsal } from "@azure/msal-react";
import {
	AlertType,
	ApplicationScreen,
	Clinic,
	ClinicSetting
} from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { ConfirmMembershipChange } from "./ConfirmMembershipChange";
import { useCreateAuditLog } from "../../hooks";

export const Membership = () => {
	const { userClinics, setRefreshClinics } = useClinicContext();
	const { account } = useUserContext();
	const { showAlert } = useAlertContext();
	const { instance } = useMsal();
	const [cllinicSettings, setClinicSettings] = useState<ClinicSetting[]>([]);
	const [membershipEnabled, setMembershipEnabled] = useState(true);
	const [newValue, setNewValue] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	useEffect(() => {
		if (userClinics) {
			const clinicSettings = userClinics.flatMap(
				(clinic: Clinic) =>
					clinic.clinicSettings?.filter(
						(setting: ClinicSetting) =>
							setting.settingName === SETTING_OM_ENABLE_MEMBERSHIP
					) ?? []
			);
			setClinicSettings(clinicSettings);
			setMembershipEnabled(
				clinicSettings.filter(
					(setting) =>
						setting.settingName === SETTING_OM_ENABLE_MEMBERSHIP &&
						setting.value === "1"
				).length > 0
			);
		}
	}, [userClinics]);

	const confirmMembershipChange = async (
		event: SyntheticEvent,
		checked: boolean
	) => {
		setNewValue(checked);
		setOpenConfirm(true);
	};

	const toggleEnableMembership = async (confirmed: boolean) => {
		if (!confirmed) {
			setNewValue(membershipEnabled);
			setOpenConfirm(false);
			return;
		}
		try {
			const requestUrl = BuildUrl("Clinic/ClinicSettings");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};
			var body = cllinicSettings?.map((setting: ClinicSetting) => ({
				id: setting.id,
				clinicId: setting.clinicId,
				settingId: setting.settingId,
				value: newValue ? "1" : "0"
			}));

			const response = await fetch(requestUrl, {
				method: "PUT",
				headers,
				body: JSON.stringify(body)
			});

			if (!response.ok) {
				throw new Error("Failed to update membership");
			}

			await callCreateAuditLog(
				cllinicSettings?.map((setting: ClinicSetting) => setting.clinicId),
				newValue
			);

			showAlert({
				message: `Membership in the Our Medical app has been ${newValue ? "enabled" : "disabled"}`,
				alertType: AlertType.success,
				timeout: true
			});
			setMembershipEnabled(newValue);
			setRefreshClinics(true);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			setNewValue(newValue);
		} catch (error) {
			showAlert({
				message: `There was an error ${newValue ? "enabling" : "disabling"} membership`,
				alertType: AlertType.error,
				timeout: true
			});
			setNewValue(membershipEnabled);
		} finally {
			setIsLoading(false);
			setOpenConfirm(false);
		}
	};

	const callCreateAuditLog = async (
		clnicIds: number[],
		membershipEnabled: boolean
	) => {
		try {
			await createAuditLog(
				clnicIds.map((clinicId: number) => ({
					clinicId: clinicId,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminCentreOperations,
					action: membershipEnabled
						? "Enabled membership"
						: "Disabled membership"
				}))
			);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	if (!isLoading) {
		return (
			<>
				<div className="flex">
					<div className="mr-12 ml-4">
						Enables Membership in Our Medical app
					</div>
					<Switch
						id="membership"
						checked={membershipEnabled}
						onChange={confirmMembershipChange}
						name="Enable Membership in Our Medical App"
					/>
				</div>
				<ConfirmMembershipChange
					open={openConfirm}
					newValue={newValue}
					onClose={toggleEnableMembership}
				/>
			</>
		);
	} else {
		return (
			<div className="flex justify-center py-3">
				<CircularProgress size="80px" />
			</div>
		);
	}
};
